import React, { useEffect, useState } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
  Divider,
  Grid,
  Chip,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Group as GroupIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import axios from 'axios';
import { usePageTitle } from '../hooks';
import { logger } from '../utils/logger';
import CreateIcpModal from '../components/icp/CreateIcpModal';
import { getIcpLimit, isUnlimitedIcp } from '../utils/planLimits';
import { useAuth } from '../contexts/AuthContext';
import OnboardingModal from '../components/OnboardingModal';
import TrialStatusModal from '../components/TrialStatusModal';
import SubscriptionExpiredModal from '../components/SubscriptionExpiredModal';
import SubscriptionCancellationBanner from '../components/SubscriptionCancellationBanner';
import ResourceLimitCard from '../components/common/ResourceLimitCard';
import CollapsibleInfoCard from '../components/common/CollapsibleInfoCard';
import useTrialExpirationHandler from '../hooks/useTrialExpirationHandler';
import { useSubscriptionExpirationHandler } from '../hooks/useSubscriptionExpirationHandler';
import { useCreateWithTrialCheck } from '../hooks/useCreateWithTrialCheck';
import DataTable, { ColumnDefinition, SortConfig } from '../components/DataTable';
import IcpDetailsModal from '../components/icp/IcpDetailsModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface ICPItem {
  id: string;
  title: string;
  data: string;
  default: boolean;
  createdAt?: string;
}

interface FilterConfig {
  query?: string;
  type?: 'all' | 'default' | 'custom';
}

// Mobile card component
const IcpCard: React.FC<{ icp: ICPItem }> = ({ icp }) => {
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>{icp.title}</Typography>
          <Chip size="small" label={icp.default ? 'Default' : 'Custom'} color={icp.default ? 'default' : 'primary'} variant={icp.default ? 'outlined' : 'filled'} />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
          {icp.data.length > 200 ? `${icp.data.slice(0, 200)}…` : icp.data}
        </Typography>

        {icp.createdAt && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Created: {new Date(icp.createdAt).toLocaleString()}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const ICPsPage: React.FC = () => {
  const [icps, setIcps] = useState<ICPItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'default', direction: 'asc' });
  const [showFilters, setShowFilters] = useState(false);
  const [filterConfig, setFilterConfig] = useState<FilterConfig>({ type: 'all', query: '' });
  const [orgInfo, setOrgInfo] = useState<any>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedIcpId, setSelectedIcpId] = useState<string | null>(null);

  const { user, showOnboarding, setShowOnboarding } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Trial expiration handling
  const {
    isExpiredModalOpen: isTrialStatusModalOpen,
    isAdmin: isTrialAdmin,
    handleApiError: handleTrialApiError,
    closeExpiredModal: closeTrialStatusModal
  } = useTrialExpirationHandler();

  // Subscription expiration handling
  const {
    isExpiredModalOpen: isSubscriptionExpiredModalOpen,
    isAdmin: isSubscriptionAdmin,
    handleApiError: handleSubscriptionApiError,
    closeExpiredModal: closeSubscriptionExpiredModal
  } = useSubscriptionExpirationHandler();
  
  // Trial check for create button
  const {
    handleCreateAction,
    isTrialStatusModalOpen: createTrialStatusModalOpen,
    closeTrialStatusModal: closeCreateTrialStatusModal,
    isAdmin: createTrialAdmin
  } = useCreateWithTrialCheck();

  // Set page title
  usePageTitle('Personas');

  // Onboarding check + org info
  useEffect(() => {
    // Removed duplicate onboarding trigger - handled centrally in AuthContext
    // if (user && !user.organizationId) {
    //   setShowOnboarding(true);
    // } else if (user && user.organizationId) {
    if (user && user.organizationId) {
      fetchOrgInfo();
    }
  }, [user]);

  // Removed local showOnboarding state - using AuthContext version
  // const [showOnboarding, setShowOnboarding] = useState(false);

  const fetchOrgInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/organization`, {
        withCredentials: true
      });
      setOrgInfo(response.data.organization);
    } catch (error) {
      if (!handleTrialApiError(error)) {
        handleSubscriptionApiError(error);
      }
      // Non-blocking
    }
  };

  const fetchIcps = async (pageNum = 1, limit = 10, signal?: AbortSignal) => {
    try {
      setLoading(true);
      const params: any = {
        page: pageNum,
        limit,
        sortBy: sortConfig.key,
        sortOrder: sortConfig.direction,
      };
      if (filterConfig.type && filterConfig.type !== 'all') params.type = filterConfig.type;
      if (filterConfig.query) params.query = filterConfig.query;
      const response = await axios.get(`${API_URL}/api/icps`, {
        withCredentials: true,
        params,
        signal
      });
      const items: ICPItem[] = (response.data.icps || []).map((i: any) => ({
        id: i.id,
        title: i.title,
        data: i.data,
        default: Boolean(i.default),
        createdAt: i.createdAt
      }));
      setIcps(items);
      setTotalCount(response.data.pagination?.total || items.length);
    } catch (err) {
      if ((err as any)?.name === 'CanceledError') return;
      if (!handleTrialApiError(err) && !handleSubscriptionApiError(err)) {
        setError('Failed to load Personas. Please try again later.');
        // eslint-disable-next-line no-console
        logger.error('Error fetching ICPs:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchIcps(page + 1, rowsPerPage, controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, sortConfig, filterConfig]);

  // Server-driven, just reset page on filter/sort changes
  useEffect(() => {
    setPage(0);
  }, [filterConfig, sortConfig]);

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };
  const handleSort = (key: string) => {
    setSortConfig((prev) =>
      prev.key === key ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'asc' }
    );
  };
  const handleFilterChange = (key: keyof FilterConfig, value: any) => {
    setFilterConfig((prev) => ({ ...prev, [key]: value }));
  };
  const clearFilters = () => setFilterConfig({ type: 'all', query: '' });

  const columns: ColumnDefinition<ICPItem>[] = [
    {
      key: 'title',
      label: 'Title',
      render: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          {/* <GroupIcon fontSize="small" /> */}
          <Typography sx={{ fontWeight: 'medium' }}>{row.title}</Typography>
        </Stack>
      )
    },
    {
      key: 'default',
      label: 'Type',
      render: (row) => (
        <Chip size="small" label={row.default ? 'Default' : 'Custom'} color={row.default ? 'default' : 'primary'} variant={row.default ? 'outlined' : 'filled'} />
      )
    },
    {
      key: 'data',
      label: 'Summary',
      sortable: false,
      render: (row) => (
        <Typography color="text.secondary">
          {row.data.length > 120 ? `${row.data.slice(0, 120)}…` : row.data}
        </Typography>
      )
    }
  ];

  const mobileSortOptions = [
    { key: 'title', label: 'Title' },
    { key: 'createdAt', label: 'Created Date' }
  ];

  const renderMobileCard = (icp: ICPItem) => <IcpCard key={icp.id} icp={icp} />;

  const emptyStateComponent = (
    <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.paper' }}>
      <GroupIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'medium' }}>
        No Personas Yet
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 1, maxWidth: 520, mx: 'auto' }}>
        You can use our default Personas or create custom ones for your organization.
      </Typography>
    </Paper>
  );

  // Plan-based create availability
  const customCount = icps.filter(i => !i.default).length;
  const planLimit = orgInfo?.icpLimit != null ? orgInfo.icpLimit : (orgInfo ? getIcpLimit(orgInfo.plan) : 0);
  const isUnlimited = orgInfo?.icpLimit == null ? (orgInfo ? isUnlimitedIcp(orgInfo.plan) : false) : orgInfo.icpLimit === Number.MAX_SAFE_INTEGER;
  const isAdmin = user?.role === 'ADMIN';
  const createDisabled = (!isAdmin) || (orgInfo && !isUnlimited && customCount >= planLimit);

  if (loading && !isTrialStatusModalOpen && !isSubscriptionExpiredModalOpen) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 64px)' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (showOnboarding) {
    return <OnboardingModal isOpen={showOnboarding} onClose={() => setShowOnboarding(false)} />;
  }

  if (error && !isTrialStatusModalOpen && !isSubscriptionExpiredModalOpen) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => fetchIcps(page + 1, rowsPerPage)} sx={{ mt: 2 }}>Retry</Button>
      </Box>
    );
  }

  return (
    <>
      {orgInfo?.status === 'ACTIVE_UNTIL' && (
        <SubscriptionCancellationBanner isAdmin={user?.role === 'ADMIN'} expiryDate={orgInfo.nextBillingAt} />
      )}

      <TrialStatusModal isOpen={isTrialStatusModalOpen} onClose={closeTrialStatusModal} isAdmin={isTrialAdmin} status="expired" />
      <SubscriptionExpiredModal isOpen={isSubscriptionExpiredModalOpen} onClose={closeSubscriptionExpiredModal} isAdmin={isSubscriptionAdmin} />
      
      {/* Trial expired modal for create button clicks */}
      <TrialStatusModal isOpen={createTrialStatusModalOpen} onClose={closeCreateTrialStatusModal} isAdmin={createTrialAdmin} status="expired" />

      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 4, bgcolor: 'background.paper' }}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 3,
          gap: { xs: 2, sm: 0 }
        }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" sx={{ mb: { xs: 0, sm: 1 } }}>
            Personas
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'stretch' : 'flex-end' }}>
            {/* {!isUnlimited && orgInfo && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, textAlign: isMobile ? 'left' : 'right' }}>
                Custom ICPs: {customCount} / {planLimit}
              </Typography>
            )} */}
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => handleCreateAction(() => setIsCreateOpen(true))} fullWidth={isMobile} sx={{ height: 'fit-content' }} disabled={!!createDisabled}>
              New Persona
            </Button>
          </Box>
        </Box>

        {/* Info */}
        <CollapsibleInfoCard
          title="About Personas"
          content="Personas capture who your content is for. Use defaults or create custom Personas for your org. Click a Persona to view details; custom Personas can be edited, defaults are read‑only."
        />

        {/* Limits */}
        {!isUnlimited && orgInfo && (
          <ResourceLimitCard
            icon={<GroupIcon sx={{ color: 'primary.main' }} />}
            title="Custom Personas"
            used={customCount}
            limit={planLimit}
            isAdmin={user?.role === 'ADMIN'}
            sx={{ mb: 2 }}
          />
        )}

        {/* Controls */}
        <Box component="section" aria-label="ICPs Controls" sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 2,
          gap: { xs: 2, sm: 0 }
        }}>
          <Typography variant="h6" component="h2">All Personas</Typography>
          <Button variant="outlined" startIcon={<FilterListIcon />} onClick={() => setShowFilters(!showFilters)} aria-expanded={showFilters} aria-controls="filters-panel" fullWidth={isMobile}>
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </Box>

        <Collapse in={showFilters}>
          <Paper component="aside" id="filters-panel" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
            <Typography variant="subtitle1" component="h3" gutterBottom>Filter by:</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField fullWidth size="small" label="Search" value={filterConfig.query || ''} onChange={(e) => handleFilterChange('query', e.target.value)} placeholder="Search by title or content" />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select value={filterConfig.type || 'all'} label="Type" onChange={(e) => handleFilterChange('type', e.target.value)}>
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="default">Default</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <Button startIcon={<ClearIcon />} onClick={clearFilters} variant="outlined" color="primary" fullWidth={isMobile}>
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Collapse>

        {/* Table */}
        <Box component="section" aria-labelledby="icps-content-heading">
          <Typography component="h2" id="icps-content-heading" sx={{ border: 0, clip: 'rect(0 0 0 0)', height: '1px', margin: '-1px', overflow: 'hidden', padding: 0, position: 'absolute', width: '1px', whiteSpace: 'nowrap' }}>
            Personas List
          </Typography>

          <DataTable
            data={icps}
            columns={columns}
            sortConfig={sortConfig}
            onSort={handleSort}
            onRowClick={(row) => setSelectedIcpId(row.id)}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            renderMobileCard={renderMobileCard}
            mobileSortOptions={mobileSortOptions}
            emptyStateComponent={emptyStateComponent}
            tableAriaLabel="icps table"
          />
        </Box>
      </Paper>

      {/* Create ICP Modal */}
      <CreateIcpModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={() => {
          fetchIcps();
        }}
      />

      {/* ICP Details/Edit Modal */}
      <IcpDetailsModal
        open={!!selectedIcpId}
        icpId={selectedIcpId}
        onClose={() => setSelectedIcpId(null)}
        onSaved={() => {
          setSelectedIcpId(null);
          fetchIcps();
        }}
      />
    </>
  );
};

export default ICPsPage;


